import { useRef, useState } from 'react';
import { useOnClickOutside } from 'usehooks-ts';
import cn from 'classnames';
import { useQuerySearchParams, useUpdateQueryParams } from 'hooks/routes';
import { FilterIcon_16 } from 'assets/icons';
import CheckboxGroup from 'components/CheckboxGroup';
import Button from 'components/Button';

import styles from './TableFilter.module.css';
import messages from './TableFilter.messages';

type TableFilterProps = {
  name: string;
  filterKey: string;
  options: [string, string][];
};

const TableFilter = ({ name, filterKey, options }: TableFilterProps): JSX.Element => {
  const [filterValue] = useQuerySearchParams<string>(filterKey);
  const [draftFilterValue, setDraftFilterValue] = useState(filterValue);

  const updateQueryParams = useUpdateQueryParams();

  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  const isActive = !!filterValue;

  const toggle = () => {
    setIsOpen((prev) => !prev);
  };

  const onCancel = () => {
    setIsOpen(false);
  };

  const onApply = () => {
    const newParams: Record<string, any> = { page: 1 };
    newParams[filterKey] = draftFilterValue;
    updateQueryParams(newParams);

    onCancel();
  };

  const onClear = () => {
    const updates: Record<string, any> = { page: 1 };
    updates[filterKey] = undefined;
    updateQueryParams(updates);

    onCancel();
  };

  useOnClickOutside(ref, onCancel);

  return (
    <span className={styles.filterCell} ref={ref}>
      {name}
      <FilterIcon_16
        className={cn(styles.filterIcon, isActive && styles.filterIconActive)}
        onClick={toggle}
      />
      {isOpen && (
        <div className={styles.wrapper}>
          <div className={styles.filterWrapper}>
            <CheckboxGroup
              options={options}
              defaultSelected={filterValue}
              onSelect={(selected) => {
                setDraftFilterValue(selected);
              }}
            />

            <div className={styles.divider} />

            <div className={styles.buttonsWrapper}>
              <Button
                onClick={onClear}
                variant={'outline'}
                disabled={!filterValue && !draftFilterValue}
              >
                {messages.clearFilterButton}
              </Button>
              <Button onClick={onApply} disabled={filterValue == draftFilterValue}>
                {messages.applyButton}
              </Button>
            </div>
          </div>
        </div>
      )}
    </span>
  );
};

export default TableFilter;
